import { css } from '@emotion/react';
import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDrop } from 'react-dnd';
import { getAlternatingColor } from '~/_shared/baseComponents/alternatingStripes/alternatingStripes';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { DndType } from '~/_shared/constants/dndType.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { type SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { compareSpreadsheetColumnIds } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { BASIC_PANEL_WIDTH } from '~/sidebar/sidebarApps/rightSidebar/rightSidebarContent.constants';
import { LOCATION_PANEL_DATA_CLASSNAME } from '../customizeLocationPanel.constants';
import {
  getColumnItemById, getLabelRelevantStyles,
} from './locationData.helpers';
import { type BubbleItemMap } from './locationData.types';
import { BubbleItemDragLayerComponent } from './locationDataItem/bubbleItemDragLayer.component';
import { DraggableItemComponent } from './locationDataItem/draggableItem.component';
import { type PanelBulkStyles } from './panelSettings.types';

const wrapperStyle = ({ fullWidth }: { fullWidth: boolean }) => css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: fullWidth ? '100%' : BASIC_PANEL_WIDTH,
});

const dropAreaStyle = css({
  position: 'relative',
  height: '100%',
  width: '100%',
});

const dataWrapperStyle = css({
  boxSizing: 'border-box',
  width: '100%',
});

const bubbleItemStyle = (theme: Theme) => css({
  '&:nth-of-type(2n)': {
    backgroundColor: getAlternatingColor(theme),
  },
});

const contactItemTitleStyle = (
  { isOffset, verticalGap, fontSize, color }: { isOffset: boolean; color: string; fontSize: string; verticalGap: string }
) => css({
  color,
  fontSize: `${fontSize}px`,
  marginBottom: `${verticalGap}px`,
  paddingLeft: isOffset ? 52 : 14,
  paddingTop: 7,
  textTransform: 'uppercase',
});

const contactItemsWrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

type LocationDataProps = Readonly<{
  allBasicItemsOrdered: ReadonlyArray<SpreadsheetColumnId>;
  allContactItemsOrdered: ReadonlyArray<SpreadsheetColumnId>;
  bubbleItems: BubbleItemMap;
  isBeingEdited: boolean;
  isMobileScreen: boolean;
  retiredOrdered: ReadonlyArray<SpreadsheetColumnId>;
  selectedItemId: SpreadsheetColumnId | null;
  bulkStyles: PanelBulkStyles;

  onEditItemValue?: (columnId: SpreadsheetColumnId, newValue: string) => void;
  onDrop: (sourceId: SpreadsheetColumnId, targetId: SpreadsheetColumnId) => void;
  onSelectItem: (itemId: SpreadsheetColumnId | null) => void;
  onHideItem: (columnId: SpreadsheetColumnId) => void;
  onShowItem: (columnId: SpreadsheetColumnId) => void;
}>;

export const LocationDataComponent: FC<LocationDataProps> = ({
  allBasicItemsOrdered,
  allContactItemsOrdered,
  bubbleItems,
  bulkStyles,
  isBeingEdited,
  isMobileScreen,
  onDrop,
  onEditItemValue,
  onHideItem,
  onSelectItem,
  onShowItem,
  retiredOrdered,
  selectedItemId,
  ...restProps
}) => {
  const [t] = useTranslation();
  const [, drop] = useDrop<{ type: string; id: SpreadsheetColumnId }, void, unknown>({
    accept: [DndType.LocalizationContactItem, DndType.LocalizationBasicItem],
  });

  const handleItemSelect = useCallback((itemId: SpreadsheetColumnId | null) => () => {
    // we need that on desktop only, on mobile devices it causes
    // an issue where opening a menu won't close if clicked on locationData component
    if (isMobileScreen) {
      return;
    }

    onSelectItem(itemId);
  }, [onSelectItem, isMobileScreen]);

  const handleVisibility = useCallback((id: SpreadsheetColumnId, isVisible: boolean) => () => {
    if (isVisible) {
      onHideItem(id);
    }
    else {
      onShowItem(id);
    }
  }, [onHideItem, onShowItem]);

  const getBubbleItems = useCallback((ids: ReadonlyArray<SpreadsheetColumnId>, alternateColours: boolean) =>
    ids.map((id) => {
      const bubbleItem = getColumnItemById(bubbleItems, id);
      const isVisible = !retiredOrdered.find(item => compareSpreadsheetColumnIds(item, id));

      if (!isVisible && !isBeingEdited) {
        return;
      }

      return bubbleItem ? (
        <div
          css={alternateColours ? bubbleItemStyle : null}
          className={LOCATION_PANEL_DATA_CLASSNAME}
          key={'' + id.spreadsheetId + '_' + id.columnId}
          onMouseDown={handleItemSelect(id)}
        >
          <DraggableItemComponent
            isDraggable={isBeingEdited}
            isSelected={isBeingEdited && !!selectedItemId && compareSpreadsheetColumnIds(selectedItemId, id)}
            item={bubbleItem}
            bulkStyles={bulkStyles}
            onEdit={onEditItemValue}
            onDrop={onDrop}
            isVisible={isVisible}
            showVisibilityIcon={isBeingEdited}
            onVisibilityIconClick={handleVisibility(id, isVisible)}
          />
        </div>
      ) : undefined;
    }).filter(notNullsy),
  [bubbleItems, retiredOrdered, isBeingEdited, handleItemSelect, selectedItemId, bulkStyles, onDrop, onEditItemValue, handleVisibility]);

  const commonBody = useMemo(() => {
    const { labelFontColor, labelFontSize, showLabel, verticalGap } = bulkStyles.contactLabelStyle ?? getLabelRelevantStyles(bulkStyles);
    const contactBubbleItems = getBubbleItems(allContactItemsOrdered, false);

    return (
      <>
        <BubbleItemDragLayerComponent bulkStyles={bulkStyles} />
        <div css={dataWrapperStyle}>
          {contactBubbleItems.length > 0 && (
            <div css={contactItemsWrapperStyle}>
              {showLabel && (
                <div
                  css={contactItemTitleStyle({
                    isOffset: isBeingEdited,
                    color: labelFontColor,
                    fontSize: labelFontSize,
                    verticalGap,
                  })}
                >
                  {t('Contact Info')}
                </div>
              )}
              {contactBubbleItems}
            </div>
          )}

          {getBubbleItems(allBasicItemsOrdered, true)}
        </div>
      </>
    );
  }, [allContactItemsOrdered, bulkStyles, isBeingEdited, t, getBubbleItems, allBasicItemsOrdered]);

  return (
    <div
      {...restProps}
      css={wrapperStyle({ fullWidth: isMobileScreen })}
    >
      <div
        css={dropAreaStyle}
        ref={el => {
          drop(el);
        }}
      >
        {isMobileScreen ? (
          commonBody
        ) : (
          <ScrollBarComponent type={ScrollbarType.Vertical}>
            {commonBody}
          </ScrollBarComponent>
        )}
      </div>
    </div>
  );
};
